import { useNavigate } from 'react-router-dom';
import STEMForgeLogo from './STEMForgeLogo';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    path: string,
    sectionId?: string
  ) => {
    e.preventDefault();
    if (sectionId) {
      if (window.location.pathname === path) {
        const el = document.getElementById(sectionId);
        if (el) {
          const offset = 80; // Account for fixed navbar
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      } else {
        sessionStorage.setItem('scrollToSection', sectionId);
        navigate(path);
      }
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const solutionsLinks = [
    { label: 'STEM Lab Setup', path: '/solutions', sectionId: 'stem-lab' },
    { label: 'AI & Robotics Lab', path: '/solutions', sectionId: 'ai-robotics' },
    { label: 'Academic Integration', path: '/academic-integration' },
    { label: 'Innovation Workshops', path: '/solutions', sectionId: 'workshops' },
  ];

  const companyLinks = [
    { label: 'About Us', path: '/', sectionId: 'about' },
    { label: 'Solutions', path: '/solutions' },
    { label: 'Academic Integration', path: '/academic-integration' },
    { label: 'Contact Us', path: '/', sectionId: 'contact' },
  ];

  const trustBadges = [
    {
      icon: 'ri-shield-check-fill',
      title: 'Quality Assured',
      subtitle: 'ISO 9001:2015',
      color: 'from-emerald-500/20 to-emerald-600/10',
      border: 'border-emerald-500/30',
      iconColor: 'text-emerald-400',
    },
    {
      icon: 'ri-award-fill',
      title: 'STEM Certified',
      subtitle: 'Govt. Recognised',
      color: 'from-amber-500/20 to-amber-600/10',
      border: 'border-amber-500/30',
      iconColor: 'text-amber-400',
    },
    {
      icon: 'ri-verified-badge-fill',
      title: 'NEP 2020 Aligned',
      subtitle: 'Ministry Approved',
      color: 'from-sky-500/20 to-sky-600/10',
      border: 'border-sky-500/30',
      iconColor: 'text-sky-400',
    },
    {
      icon: 'ri-lock-fill',
      title: 'Data Secure',
      subtitle: 'SSL Encrypted',
      color: 'from-violet-500/20 to-violet-600/10',
      border: 'border-violet-500/30',
      iconColor: 'text-violet-400',
    },
  ];

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white rounded-t-[40px] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1 md:col-span-2">
            <STEMForgeLogo height={80} gradientId="footer-logo-grad" className="mb-4" />
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Transforming schools into STEM innovation hubs and preparing students for tech-driven careers aligned with industry needs.
            </p>
            <div className="flex space-x-4">
              {[
                { icon: 'ri-linkedin-fill', href: 'https://linkedin.com/company/stemforge-tech-labs', label: 'LinkedIn' },
                { icon: 'ri-threads-fill', href: 'https://www.threads.com/@stemforgeofficial', label: 'Threads' },
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/stemforgeofficial?igsh=MWlhbzU2djFycW9oaw%3D%3D&utm_source=qr', label: 'Instagram' },
                { icon: 'ri-youtube-fill', href: 'https://www.youtube.com/@stemforgetechlabs', label: 'YouTube' },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg hover:bg-emerald-600 transition-all cursor-pointer"
                >
                  <i className={`${social.icon} text-xl`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Solutions Column */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Solutions
            </h3>
            <ul className="space-y-3">
              {solutionsLinks.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.sectionId ? `${item.path}#${item.sectionId}` : item.path}
                    onClick={(e) => handleNavClick(e, item.path, item.sectionId)}
                    className="text-white hover:text-emerald-400 transition-all hover:translate-x-1 inline-block cursor-pointer"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.sectionId ? `${item.path}#${item.sectionId}` : item.path}
                    onClick={(e) => handleNavClick(e, item.path, item.sectionId)}
                    className="text-white hover:text-emerald-400 transition-all hover:translate-x-1 inline-block cursor-pointer"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Get Started
            </h3>
            <div className="space-y-4">
              <a
                href="mailto:info@stemforgetechlabs.in"
                className="text-emerald-400 hover:text-emerald-300 block cursor-pointer"
              >
                info@stemforgetechlabs.in
              </a>
              <p className="text-sm text-gray-400 leading-relaxed">
                Puducherry, India
              </p>
            </div>
          </div>
        </div>

        {/* Trust & Certification Badges */}
        <div className="border-t border-white/10 pt-10 mb-8">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-gray-500 mb-6">
            Trusted, Certified &amp; Recognised
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {trustBadges.map((badge) => (
              <div
                key={badge.title}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-br ${badge.color} border ${badge.border} backdrop-blur-sm`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 shrink-0`}>
                  <i className={`${badge.icon} text-xl ${badge.iconColor}`}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white leading-tight">{badge.title}</p>
                  <p className="text-xs text-gray-400 leading-tight mt-0.5">{badge.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">
              © {currentYear} STEMForge Tech Labs. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <a href="#" className="hover:text-white cursor-pointer whitespace-nowrap transition-colors">Privacy Policy</a>
              <span className="text-gray-600">|</span>
              <a href="#" className="hover:text-white cursor-pointer whitespace-nowrap transition-colors">Terms of Service</a>
              <span className="text-gray-600">|</span>
              <a href="#" className="hover:text-white cursor-pointer whitespace-nowrap transition-colors">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
